import { useState } from 'react';
import { InteractionManager } from 'react-native';
import { useContainerPositionRef } from '@v2/pages/common/hooks/useContainerPositionRef';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import {
  selectCurrentContextImagesDocuments,
  selectCurrentImageDocumentContext,
  selectSelectedNote,
} from '../../../../../../../reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { removeImageDocument } from '@reducers/v2/imageHandlingV2/thunks';
import { useManageDocTypeButton } from '@v2/components/imageHandling/components/Header/components/DocTypeButton/hooks/useManageDocTypeButton';
import i18n from 'i18n-js';
import { ActionsProps } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationNotesScreen/Actions';
import { deletePrompt } from '@helpers';
import { useManageAttachments } from '@components/common/attachButton/hooks/useManageAttachments';
import { useTabCount } from '@v2/pages/common/hooks/useTabCount';
import { DOC_TYPE } from '@components/imageHandling/common';

const ACTION_MENU_ICON_SIZE = 18;
const ACTION_MENU_PRIMARY_COLOR = 'Label/Light/Primary';

interface UseManageOPNotesActionsParams extends Pick<ActionsProps, 'onRename' | 'documentId' | 'removeNote'> {}

export const useManageOPNotesActions = ({ onRename, documentId, removeNote }: UseManageOPNotesActionsParams) => {
  const [rootRef, rootPosition] = useContainerPositionRef();
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [attachModalOpen, setAttachModalOpen] = useState(false);

  const dispatch = useAppDispatch();
  const currentContext = useSelector(selectCurrentImageDocumentContext);
  const selectedNote = useSelector(selectSelectedNote);

  const currentContextImagesDocuments = useSelector(selectCurrentContextImagesDocuments);

  const deleteDocument = (imageDocument: ImageDocumentStoreItem) =>
    dispatch(removeImageDocument({ documentId: imageDocument.documentId, imageDocument, imageContext: currentContext }));

  const { onSuccessDocTypeChange, onSuccessImageRemove } = useTabCount();

  const { toggleDocTypeModal } = useManageDocTypeButton({ images: currentContextImagesDocuments, img: selectedNote, onSuccessDocTypeChange });

  const actionsConfig = [
    {
      lable: {
        color: ACTION_MENU_PRIMARY_COLOR,
        name: i18n.t('Rename'),
      },
      icon: {
        name: 'edit',
        width: ACTION_MENU_ICON_SIZE,
        height: ACTION_MENU_ICON_SIZE,
        color: ACTION_MENU_PRIMARY_COLOR,
      },
      onPress: () => {
        setActionsMenuOpen(false);
        onRename();
      },
      ignore: !documentId,
    },
    {
      lable: {
        color: ACTION_MENU_PRIMARY_COLOR,
        name: i18n.t('Share'),
      },
      icon: {
        name: 'share',
        width: ACTION_MENU_ICON_SIZE,
        height: ACTION_MENU_ICON_SIZE,
        color: ACTION_MENU_PRIMARY_COLOR,
      },
      onPress: () => {},
      ignore: false,
    },
    {
      lable: {
        color: ACTION_MENU_PRIMARY_COLOR,
        name: i18n.t('Modify Attached To'),
      },
      icon: {
        name: 'attach',
        width: ACTION_MENU_ICON_SIZE,
        height: ACTION_MENU_ICON_SIZE,
        color: ACTION_MENU_PRIMARY_COLOR,
      },
      onPress: () => {
        setActionsMenuOpen(false);
        setAttachModalOpen(true);
      },
      ignore: !documentId,
    },
    {
      lable: {
        color: ACTION_MENU_PRIMARY_COLOR,
        name: i18n.t('Modify Document Type'),
      },
      icon: {
        name: 'document',
        width: ACTION_MENU_ICON_SIZE,
        height: ACTION_MENU_ICON_SIZE,
        color: ACTION_MENU_PRIMARY_COLOR,
      },
      onPress: () => {
        setActionsMenuOpen(false);
        toggleDocTypeModal();
      },
      ignore: false,
    },
    {
      lable: {
        color: 'Label/Light/Warning',
        name: i18n.t('Delete'),
      },
      icon: {
        name: 'remove',
        width: ACTION_MENU_ICON_SIZE,
        height: ACTION_MENU_ICON_SIZE,
        color: 'Label/Light/Warning',
      },
      onPress: async () => {
        if (!documentId) {
          return;
        }

        setActionsMenuOpen(false);
        // Wait for the action-menu modal's dismiss animation to finish before opening
        // the confirmation Alert — stacking two modals on iPad otherwise freezes the app.
        InteractionManager.runAfterInteractions(() => {
          deletePrompt({
            imageContext: currentContext,
            images: currentContextImagesDocuments,
            selectedIndex: currentContextImagesDocuments.findIndex(
              (imageDocument) => imageDocument.id === documentId || imageDocument.documentId === documentId
            ),
            setSelectedIndex: () => {},
            deleteImage: deleteDocument,
            onSuccessImageRemove: () => {
              removeNote?.(documentId);
              onSuccessImageRemove(DOC_TYPE.NOTE);
            },
          });
        });
      },
      ignore: !documentId,
    },
  ];

  const {
    handleToggleFavoriteLink,
    handleStartRemovingAttachment,
    showRemoveAttachmentModal,
    setShowRemoveAttachmentModal,
    handleConfirmRemoveAttachment,
  } = useManageAttachments({ img: selectedNote });

  return {
    rootRef,
    actionsMenuOpen,
    rootPosition,
    actionsConfig,
    attachModalOpen,
    setAttachModalOpen,
    setActionsMenuOpen,
    selectedNote,
    handleToggleFavoriteLink,
    showRemoveAttachmentModal,
    setShowRemoveAttachmentModal,
    handleConfirmRemoveAttachment,
    handleStartRemovingAttachment,
  };
};
